import { userErrorMessage } from '@wx-weiqi/contracts';
import { useId, useRef, useState } from 'react';
import { BookmarkIcon, Pencil1Icon, PlusIcon } from '@radix-ui/react-icons';
import {
  ebookChapterRenameProblem,
  newEbookChapterProblem,
} from '../services/ebook-chapter-editing';
import './ebook-chapter-select.css';

const CREATE_CHAPTER = '__create_ebook_chapter__';

export function EbookChapterSelect({
  chapter,
  chapters,
  disabled,
  onChange,
  onCreate,
  onRename,
  questionCount,
}: {
  chapter: string;
  chapters: readonly string[];
  disabled: boolean;
  onChange: (chapter: string) => void;
  onCreate: (name: string) => Promise<void>;
  onRename: (currentName: string, nextName: string) => Promise<void>;
  questionCount?: number;
}) {
  const id = useId();
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState('');
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const inFlight = useRef(false);
  const select = useRef<HTMLSelectElement>(null);
  const input = useRef<HTMLInputElement>(null);
  const submit = async () => {
    if (disabled || inFlight.current) return;
    const problem = newEbookChapterProblem(name, chapters);
    setNotice('');
    setError(problem);
    if (problem) {
      input.current?.focus();
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      await onCreate(name.trim());
      setCreating(false);
      setName('');
      setNotice(`“${name.trim()}”已创建并用于本题。`);
    } catch (failure) {
      setError(userErrorMessage(failure, '章节未保存，请保留名称并稍后重试。'));
    } finally {
      inFlight.current = false;
      setPending(false);
      requestAnimationFrame(() => {
        if (input.current) input.current.focus();
        else select.current?.focus();
      });
    }
  };
  const submitRename = async () => {
    if (disabled || inFlight.current || !chapter) return;
    const problem = ebookChapterRenameProblem(name, chapter, chapters);
    setNotice('');
    setError(problem);
    if (problem) {
      input.current?.focus();
      return;
    }
    inFlight.current = true;
    setPending(true);
    try {
      await onRename(chapter, name.trim());
      setEditing(false);
      setNotice(`章节已修改为“${name.trim()}”，本章题目已同步更新。`);
      setName('');
    } catch (failure) {
      setError(userErrorMessage(failure, '章节未保存，请保留名称并稍后重试。'));
    } finally {
      inFlight.current = false;
      setPending(false);
      requestAnimationFrame(() => {
        if (input.current) input.current.focus();
        else select.current?.focus();
      });
    }
  };
  const beginRename = () => {
    if (disabled || !chapter) return;
    setCreating(false);
    setError('');
    setNotice('');
    setName(chapter);
    setEditing(true);
    requestAnimationFrame(() => input.current?.select());
  };
  return (
    <div className="ebook-chapter-select" aria-busy={pending}>
      <label className="ebook-chapter-select-label" htmlFor={`${id}-select`}>
        <BookmarkIcon aria-hidden="true" />
        所属章节
      </label>
      <select
        ref={select}
        id={`${id}-select`}
        value={chapter ? `chapter:${chapter}` : ''}
        disabled={disabled || pending}
        aria-describedby={`${id}-help`}
        onChange={(event) => {
          const value = event.target.value;
          setError('');
          setNotice('');
          if (value === CREATE_CHAPTER) {
            setCreating(true);
            requestAnimationFrame(() => input.current?.focus());
          } else {
            onChange(value ? value.slice('chapter:'.length) : '');
            setCreating(false);
            setEditing(false);
            setName('');
          }
        }}
      >
        <option value="">未分章</option>
        {chapters.map((item) => (
          <option key={item} value={`chapter:${item}`}>
            {item}
          </option>
        ))}
        <option value={CREATE_CHAPTER}>＋新增章节</option>
      </select>
      <div className="ebook-chapter-actions">
        <button
          type="button"
          className="ebook-chapter-edit-button"
          disabled={disabled || pending}
          onClick={() => {
            setCreating(true);
            setEditing(false);
            setName('');
            setError('');
            setNotice('');
            requestAnimationFrame(() => input.current?.focus());
          }}
        >
          <PlusIcon aria-hidden="true" />
          新增
        </button>
        <button
          type="button"
          className="ebook-chapter-edit-button"
          disabled={disabled || pending || !chapter}
          onClick={beginRename}
        >
          <Pencil1Icon aria-hidden="true" />
          修改章节
        </button>
      </div>
      <p id={`${id}-help`} className="ebook-muted">
        {chapters.length ? '本书共享章节，随题目一起保存。' : '本书还没有章节，可从下拉菜单新增。'}
      </p>
      {(creating || editing) && (
        <div
          className="ebook-chapter-create"
          role="group"
          aria-label={editing ? '修改章节' : '新增章节'}
        >
          <label htmlFor={`${id}-name`}>{editing ? '章节名称' : '新章节名称'}</label>
          <input
            ref={input}
            id={`${id}-name`}
            value={name}
            placeholder="例如：第一章 基础死活"
            disabled={disabled || pending}
            aria-invalid={Boolean(error)}
            aria-describedby={`${id}-save-help${error ? ` ${id}-error` : ''}`}
            onChange={(event) => {
              setName(event.target.value);
              setError('');
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !event.nativeEvent.isComposing) {
                event.preventDefault();
                void (editing ? submitRename() : submit());
              }
              if (event.key === 'Escape' && !pending) {
                setCreating(false);
                setEditing(false);
                setError('');
                select.current?.focus();
              }
            }}
          />
          <p id={`${id}-save-help`} className="ebook-muted">
            {editing
              ? `本章${questionCount === undefined ? '' : ` ${questionCount} 道`}题目同步更新。已发布内容需更新电子书后生效。`
              : '最多 120 个字符。创建时会保存当前修改，并将本题归入新章节。'}
          </p>
          {error && (
            <p id={`${id}-error`} role="alert" className="ebook-error">
              {error}
            </p>
          )}
          <div className="ebook-actions">
            <button
              type="button"
              className="primary"
              disabled={disabled || pending}
              onClick={() => void (editing ? submitRename() : submit())}
            >
              {editing ? null : <PlusIcon aria-hidden="true" />}
              {pending ? '正在保存…' : editing ? '保存章节' : '保存并使用章节'}
            </button>
            <button
              type="button"
              disabled={disabled || pending}
              onClick={() => {
                setCreating(false);
                setEditing(false);
                setError('');
                select.current?.focus();
              }}
            >
              取消
            </button>
          </div>
        </div>
      )}
      {notice && (
        <p role="status" className="ebook-chapter-saved">
          {notice}
        </p>
      )}
    </div>
  );
}
